import api from '../utils/api';
import {
  Subscriber,
  SubscriberStats,
  Blog,
  BlogStats,
  BlogContentImage,
  Campaign,
  CampaignStats,
  GenerateBlogRequest,
  GenerateImageRequest,
  GeneratedImageResponse,
  UploadedImageResponse,
  ImagePlacementSuggestion,
  GenerateCampaignRequest,
  GeneratedCampaignContent,
  ApiResponse
} from '../types/marketing.types';

// Subscriber API
export const subscriberApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    tag?: string;
    search?: string;
  }): Promise<ApiResponse<Subscriber[]>> => {
    const response = await api.get('/subscribers', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Subscriber>> => {
    const response = await api.get(`/subscribers/${id}`);
    return response.data;
  },

  create: async (data: Partial<Subscriber>): Promise<ApiResponse<Subscriber>> => {
    const response = await api.post('/subscribers', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Subscriber>): Promise<ApiResponse<Subscriber>> => {
    const response = await api.put(`/subscribers/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/subscribers/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<SubscriberStats>> => {
    const response = await api.get('/subscribers/stats');
    return response.data;
  },

  import: async (subscribers: Array<Partial<Subscriber>>): Promise<ApiResponse<{
    imported: number;
    skipped: number;
    errors: string[];
  }>> => {
    const response = await api.post('/subscribers/import', { subscribers });
    return response.data;
  },

  getByLeadId: async (leadId: string, email?: string): Promise<ApiResponse<Subscriber[]>> => {
    const params = email ? { email } : {};
    const response = await api.get(`/subscribers/by-lead/${leadId}`, { params });
    return response.data;
  },

  linkToLead: async (subscriberId: string, leadId: string): Promise<ApiResponse<Subscriber>> => {
    const response = await api.put(`/subscribers/${subscriberId}/link-lead`, { leadId });
    return response.data;
  }
};

// Blog API
export const blogApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    tag?: string;
    category?: string;
    search?: string;
    author?: string;
  }): Promise<ApiResponse<Blog[]>> => {
    const response = await api.get('/blogs', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Blog>> => {
    const response = await api.get(`/blogs/${id}`);
    return response.data;
  },

  getBySlug: async (slug: string): Promise<ApiResponse<Blog>> => {
    const response = await api.get(`/blogs/${slug}`);
    return response.data;
  },

  create: async (data: Partial<Blog>): Promise<ApiResponse<Blog>> => {
    const response = await api.post('/blogs', data);
    return response.data;
  },

  generateWithAI: async (request: GenerateBlogRequest): Promise<ApiResponse<Blog>> => {
    const response = await api.post('/blogs/generate', request);
    return response.data;
  },

  update: async (id: string, data: Partial<Blog>): Promise<ApiResponse<Blog>> => {
    const response = await api.put(`/blogs/${id}`, data);
    return response.data;
  },

  publish: async (id: string): Promise<ApiResponse<Blog>> => {
    const response = await api.put(`/blogs/${id}/publish`);
    return response.data;
  },

  unpublish: async (id: string): Promise<ApiResponse<Blog>> => {
    const response = await api.put(`/blogs/${id}/unpublish`);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/blogs/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<BlogStats>> => {
    const response = await api.get('/blogs/stats');
    return response.data;
  },

  incrementViewCount: async (id: string): Promise<void> => {
    await api.post(`/blogs/public/${id}/view`);
  },

  generateImage: async (request: GenerateImageRequest): Promise<ApiResponse<GeneratedImageResponse>> => {
    const response = await api.post('/blogs/generate-image', request);
    return response.data;
  },

  uploadImage: async (file: File): Promise<ApiResponse<UploadedImageResponse>> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/blogs/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  suggestImagePlacement: async (contentMarkdown: string, imageDescription: string): Promise<ApiResponse<ImagePlacementSuggestion>> => {
    const response = await api.post('/blogs/suggest-image-placement', { contentMarkdown, imageDescription });
    return response.data;
  },

  addContentImage: async (blogId: string, imageData: Partial<BlogContentImage>): Promise<ApiResponse<Blog>> => {
    const response = await api.post(`/blogs/${blogId}/images`, imageData);
    return response.data;
  },

  updateContentImage: async (blogId: string, imageId: string, updates: Partial<BlogContentImage>): Promise<ApiResponse<Blog>> => {
    const response = await api.put(`/blogs/${blogId}/images/${imageId}`, updates);
    return response.data;
  },

  removeContentImage: async (blogId: string, imageId: string): Promise<ApiResponse<Blog>> => {
    const response = await api.delete(`/blogs/${blogId}/images/${imageId}`);
    return response.data;
  }
};

// Campaign API
export const campaignApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
  }): Promise<ApiResponse<Campaign[]>> => {
    const response = await api.get('/campaigns', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },

  create: async (data: Partial<Campaign>): Promise<ApiResponse<Campaign>> => {
    const response = await api.post('/campaigns', data);
    return response.data;
  },

  generateWithAI: async (request: GenerateCampaignRequest): Promise<ApiResponse<GeneratedCampaignContent>> => {
    const response = await api.post('/campaigns/generate', request);
    return response.data;
  },

  update: async (id: string, data: Partial<Campaign>): Promise<ApiResponse<Campaign>> => {
    const response = await api.put(`/campaigns/${id}`, data);
    return response.data;
  },

  getRecipientPreview: async (id: string): Promise<ApiResponse<{
    count: number;
    preview: Array<{ email: string; name: string }>;
    hasMore: boolean;
  }>> => {
    const response = await api.get(`/campaigns/${id}/recipients`);
    return response.data;
  },

  send: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.post(`/campaigns/${id}/send`);
    return response.data;
  },

  schedule: async (id: string, scheduledAt: Date): Promise<ApiResponse<Campaign>> => {
    const response = await api.post(`/campaigns/${id}/schedule`, { scheduledAt });
    return response.data;
  },

  unschedule: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.post(`/campaigns/${id}/unschedule`);
    return response.data;
  },

  cancel: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.post(`/campaigns/${id}/cancel`);
    return response.data;
  },

  retryFailed: async (id: string): Promise<ApiResponse<Campaign>> => {
    const response = await api.post(`/campaigns/${id}/retry-failed`);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<CampaignStats>> => {
    const response = await api.get('/campaigns/stats');
    return response.data;
  }
};
